// inv/src/components/Header.jsx
import { useMemo } from 'react';
import { API_URL, CONTACT_PHONE, SOCIAL_WHATSAPP } from '../config';
import { useAuth } from './authentication/AuthContext.jsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languagePrefixes } from '../i18n.js';
import { handleLogout } from './authentication/logout.jsx';
import axios from 'axios';
import logo2 from '../crino_logo.svg';
import WhatsAppIcon from '../icons/whatsapp.svg';
import HeaderMenu from './ui/HeaderMenu.jsx';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, loading } = useAuth();

  const authenticated = !!user;
  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const prefix = languagePrefixes[currentLanguage] || '';
  const domain = typeof window !== 'undefined' ? window.location.origin : '';

  const handleLogoutAndUpdateUser = async () => {
    try {
      await handleLogout();
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error(error);
    }
  };

  const handleSignUp = async () => {
    try {
      await axios.get(`${API_URL}/clear-cookie`, { withCredentials: true });
      navigate(
        `${prefix}/signup?utm_source=crino-header&utm_medium=signup-button`
      );
    } catch (error) {
      console.error(error);
    }
  };

  const changeLanguage = (lng) => {
    const currentLang = (i18n.language || 'en').split('-')[0];
    const newLanguage = lng || 'en';

    const newPath = location.pathname.replace(
      languagePrefixes[currentLang],
      languagePrefixes[newLanguage]
    );

    i18n.changeLanguage(newLanguage);
    navigate(newPath);
  };

  /*
    * Menu structure.
    * type: 'link' | 'dropdown'
    * - link: single menu item
    * - dropdown: dropdown list with subitems
    */
  const menuItems = useMemo(
    () => [
      { type: 'link', path: '/', label: t('menu.home') },
      {
        type: 'dropdown',
        label: t('menu.solutions') || 'Solutions',
        items: [
          {
            path: '/for-early-stage-startups/',
            label: t('menu.solution1') || 'For early-stage startups',
          },
          {
            path: '/for-established-businesses/',
            label: t('menu.solution2') || 'For established businesses',
          },
          {
            path: '/for-accelerators-and-incubators/',
            label: t('menu.solution3') || 'For accelerators & incubators',
          },
        ],
      },
      { type: 'link', path: '/pricing/', label: t('menu.pricing') },
      { type: 'link', path: '/faq/', label: t('menu.faq') },
      { type: 'link', path: '/blog/', label: t('menu.blog') },
    ],
    [t]
  );

  return (
    <header className="mx-auto px-8 pt-6 sm:px-16">
      <nav className="mx-auto flex items-center justify-between flex-wrap">
        {/* Logo + brand */}
        <div className="group flex flex-col items-start gap-1">
          <div className="flex items-center space-x-6">
            <Link
              to={`${prefix}/`}
              className="flex items-center justify-center space-x-2"
            >
              <img
                src={logo2}
                loading="lazy"
                alt="crino.io logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <span className="text-[25px] text-left">Crino</span>
          </div>

          <div className="flex max-w-[min(82vw,420px)] flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-600">
            <img src={WhatsAppIcon} alt="" className="h-4 w-4 shrink-0" aria-hidden="true" />
            <a
              href={SOCIAL_WHATSAPP}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-stone-800 hover:text-stone-950"
              aria-label={t('header.whatsappAria', { phone: CONTACT_PHONE })}
            >
              {CONTACT_PHONE}
            </a>
            <span className="hidden sm:inline text-stone-500">
              {t('header.whatsappHint')}
            </span>
            <a
              href={SOCIAL_WHATSAPP}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-7 items-center rounded-md border border-stone-300 bg-white px-2.5 text-xs font-medium text-stone-900 shadow-sm transition duration-150 hover:border-stone-400 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 md:translate-y-1 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 md:group-focus-within:translate-y-0 md:group-focus-within:opacity-100"
            >
              {t('header.whatsappCta')}
            </a>
          </div>
        </div>

        {/* All other menu UI is moved to a separate component */}
        <HeaderMenu
          menuItems={menuItems}
          prefix={prefix}
          locationPathname={location.pathname}
          currentLanguage={currentLanguage}
          changeLanguage={changeLanguage}
          loading={loading}
          authenticated={authenticated}
          user={user}
          handleLogoutAndUpdateUser={handleLogoutAndUpdateUser}
          domain={domain}
          languagePrefixes={languagePrefixes}
          onSignUp={handleSignUp}
          signUpLabel={t('menu.signUp') || 'Sign up'}
        />
      </nav>
    </header>
  );
};

export default Header;
